import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { cn } from '../utils/cn';

// =============================================================================
// Kbd — keycap chip used inside buttons and hints
// =============================================================================
export function Kbd({
  children,
  subtle = false,
  className,
}: {
  children: ReactNode;
  subtle?: boolean;
  className?: string;
}) {
  return (
    <kbd
      className={cn(
        'inline-flex items-center justify-center font-mono font-medium rounded-sm',
        subtle
          ? 'text-[12px] px-[6px] py-[1px] bg-black/30 opacity-60 ml-1'
          : 'text-[12px] min-w-[22px] text-center px-[6px] py-[2px] bg-panel-2 border border-line border-b-2 text-text',
        className,
      )}
    >
      {children}
    </kbd>
  );
}

// =============================================================================
// Button — .btn / .btn--primary / .btn--ghost
// =============================================================================
type ButtonVariant = 'default' | 'primary' | 'ghost' | 'danger';

export function Button({
  variant = 'default',
  kbd,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  kbd?: string;
}) {
  // 비활성 + title은 '지금은 못 하는 이유'(검수 P1·useWriteGate). 기본 title 말풍선은 긴 사유가
  // 잘려 보여서(2026-10-01 대교) aria-description으로 옮긴다 — HoverTipLayer(앱 루트)가 말풍선을 띄운다.
  // 접근 가능한 이름은 그대로(테스트·보조기기가 라벨로 찾는다). 켜진 버튼의 짧은 title은 그대로 둔다.
  const reason = rest.disabled && rest.title ? rest.title : undefined;
  const base =
    'inline-flex items-center gap-1.5 px-3.5 py-2 text-[16px] font-medium rounded tracking-[-0.005em] cursor-pointer transition-all duration-fast ease-snap disabled:opacity-40 disabled:cursor-not-allowed ' +
    // 포커스 표시는 여기 한 곳에서 — 인스턴스마다 브라우저 기본 outline이 섞여 나오던 것(검수 C3).
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring focus-visible:ring-offset-1 focus-visible:ring-offset-bg';
  const variants: Record<ButtonVariant, string> = {
    default:
      'bg-transparent border border-line text-text hover:border-line-strong hover:bg-panel',
    primary:
      // 흰 글자 대비 4.6:1을 위해 채워진 면은 accent-solid (검수 S7).
      'bg-accent-solid border border-accent-solid text-white font-semibold hover:bg-accent-solid-hover hover:border-accent-solid-hover',
    ghost:
      'bg-transparent border border-transparent text-text-dim hover:text-text hover:bg-panel',
    danger:
      'bg-red/15 border border-red/50 text-red font-semibold hover:bg-red/25 hover:border-red',
  };
  return (
    <button className={cn(base, variants[variant], className)} {...rest}
      {...(reason ? { title: undefined, 'aria-description': reason } : {})}
      // 마우스로 누른 버튼에 포커스 링이 남지 않게 한다. 키보드 조작(Enter/Space)은
      // pointerup이 없으므로 링이 그대로 유지된다.
      onPointerUp={event => { rest.onPointerUp?.(event); if (!event.defaultPrevented) event.currentTarget.blur(); }}>
      {children}
      {kbd ? <Kbd subtle>{kbd}</Kbd> : null}
    </button>
  );
}

// =============================================================================
// Segmented — mutually exclusive selector (.seg)
// =============================================================================
export function Segmented<V extends string | number>({
  value,
  options,
  onChange,
  className,
}: {
  value: V;
  options: ReadonlyArray<{ value: V; label: ReactNode }>;
  onChange: (next: V) => void;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'inline-flex bg-panel-2 border border-line rounded p-[2px]',
        className,
      )}
      role="radiogroup"
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={String(opt.value)}
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              'px-3 py-[6px] text-[14px] font-medium rounded-[4px] transition-all duration-fast ease-snap',
              active
                ? 'bg-panel-3 text-accent shadow-[inset_0_0_0_1px_rgba(242,169,62,0.45)]'
                : 'bg-transparent text-text-dim hover:text-text',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// =============================================================================
// Chip — independently toggleable (.chip), with optional glyph
// =============================================================================
export function Chip({
  active = false,
  glyph,
  children,
  onClick,
  className,
}: {
  active?: boolean;
  glyph?: ReactNode;
  children: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-[6px] px-2.5 py-[6px] text-[14px] rounded-[4px] border transition-all duration-fast ease-snap cursor-pointer',
        active
          ? 'text-accent border-accent-ring bg-accent-soft'
          : 'text-text-dim border-line bg-transparent hover:text-text hover:border-line-strong',
        className,
      )}
    >
      {glyph ? (
        <span className="font-mono text-[12px] opacity-70">{glyph}</span>
      ) : null}
      {children}
    </button>
  );
}

// =============================================================================
// CtrlLabel — small uppercase mono label used on control strip
// =============================================================================
export function CtrlLabel({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'font-mono text-[12px] font-medium uppercase tracking-mono text-text-dim whitespace-nowrap',
        className,
      )}
    >
      {children}
    </span>
  );
}

// =============================================================================
// Field — label + input (with optional accent tint for coord fields)
// =============================================================================
export function Field({
  label,
  accent = false,
  className,
  inputClassName,
  ...inputProps
}: InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  accent?: boolean;
  inputClassName?: string;
}) {
  return (
    <label className={cn('block', className)}>
      <span className="block font-mono text-[12px] font-medium uppercase tracking-monoWide text-text-dim mb-1.5">
        {label}
      </span>
      <input
        {...inputProps}
        className={cn(
          'w-full px-2.5 py-2 bg-panel-2 border border-line rounded text-[17px] font-mono transition-colors duration-fast ease-snap',
          'focus:outline-none focus:border-accent focus:shadow-[0_0_0_3px_rgba(242,169,62,0.12)]',
          accent ? 'text-accent' : 'text-text',
          inputClassName,
        )}
      />
    </label>
  );
}

// =============================================================================
// Panel — card container with optional header (.panel)
// =============================================================================
export function Panel({
  title,
  trailing,
  children,
  className,
  bodyClassName,
  trailingClassName,
}: {
  title?: ReactNode;
  trailing?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  /** Extra classes for the trailing slot, e.g. `flex-1` to let a control row take the rest of the line. */
  trailingClassName?: string;
}) {
  return (
    <section
      className={cn(
        'bg-panel border border-line rounded-md overflow-hidden',
        className,
      )}
    >
      {/* 좁으면 제목과 조작부가 두 줄로 나뉜다 — 제목이 낱자로 꺾이지 않게 한 줄 고정. */}
      {(title || trailing) && (
        <header className="flex flex-wrap justify-between items-center gap-2 px-4 py-3 border-b border-line bg-panel-2">
          {title ? (
            <span className="whitespace-nowrap font-mono text-[12px] font-semibold uppercase tracking-monoWide text-text-dim">
              {title}
            </span>
          ) : (
            <span />
          )}
          {trailing ? <div className={cn('max-w-full', trailingClassName)}>{trailing}</div> : null}
        </header>
      )}
      <div className={cn('p-4', bodyClassName)}>{children}</div>
    </section>
  );
}

// =============================================================================
// StatusDot + StatusBadge — online indicator with pulsing dot
// =============================================================================
export function StatusBadge({
  tone = 'green',
  children,
}: {
  tone?: 'green' | 'red' | 'dim';
  children: ReactNode;
}) {
  const dot =
    tone === 'green'
      ? 'bg-green shadow-green-glow animate-status-pulse'
      : tone === 'red'
        ? 'bg-red'
        : 'bg-text-dimmer';
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-panel border border-line rounded font-mono text-[13px] uppercase tracking-[0.08em] text-text-dim">
      <span className={cn('w-1.5 h-1.5 rounded-full', dot)} />
      <span>{children}</span>
    </div>
  );
}

// =============================================================================
// SaveStatusBadge — 3-state (saved / unsaved / new), CALIBRATION_UX §6.7
// =============================================================================
export function SaveStatusBadge({
  status,
}: {
  status: 'saved' | 'unsaved' | 'new';
}) {
  const map = {
    saved: { cls: 'text-green', dot: 'bg-green', label: 'Saved' },
    unsaved: { cls: 'text-red', dot: 'bg-red', label: 'Unsaved' },
    new: { cls: 'text-blue', dot: 'bg-blue', label: 'New' },
  } as const;
  const entry = map[status];
  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1.5 font-mono text-[13px] uppercase tracking-[0.1em]',
        entry.cls,
      )}
    >
      <span className={cn('w-1.5 h-1.5 rounded-full', entry.dot)} />
      <span>{entry.label}</span>
    </div>
  );
}

// =============================================================================
// SearchInput — 공용 검색바 (Figma 컴포넌트 가이드).
//   Default → Hover/Focus(전역 CSS 상태) → Typing(✕ clear 노출) → 돋보기 고정.
//   value/onChange 제어형. onSearch가 있으면 Enter에서 호출.
// =============================================================================
export function SearchInput({
  value,
  onChange,
  onSearch,
  placeholder = '검색',
  className,
  inputClassName,
}: {
  value: string;
  onChange: (v: string) => void;
  onSearch?: (() => void) | undefined;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
}): JSX.Element {
  return (
    <div className={cn('relative inline-flex items-center', className)}>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && onSearch) onSearch();
        }}
        placeholder={placeholder}
        className={cn(
          'w-full rounded border border-line bg-panel-2 py-2 pl-3 text-[14px] text-text placeholder:text-text-faint',
          value ? 'pr-[60px]' : 'pr-9',
          inputClassName,
        )}
      />
      {/* Typing 상태 — clear ✕ */}
      {value && (
        <button
          type="button"
          aria-label="지우기"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onChange('')}
          className="absolute right-8 flex h-5 w-5 items-center justify-center rounded-full bg-text-faint/60 text-[10px] leading-none text-bg hover:bg-text-dim"
        >
          ✕
        </button>
      )}
      {/* 돋보기 */}
      <span
        role={onSearch ? 'button' : undefined}
        onClick={onSearch}
        className={cn(
          'absolute right-2.5 text-text-dim',
          onSearch && 'cursor-pointer hover:text-text',
        )}
        aria-label="검색"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.5" y2="16.5" />
        </svg>
      </span>
    </div>
  );
}
